// Smart multi-media download helpers — tries Web Share API on mobile (best for
// saving to Photos / camera roll), falls back to sequential blob downloads
// (best for desktop), and exposes a server-side ZIP path for big batches.
import axios from "axios";
import { API, SESSION_KEY } from "./api";

function authHeaders() {
  const token = localStorage.getItem(SESSION_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function canWebShareFiles(sampleFile) {
  try {
    if (typeof navigator === "undefined" || !navigator.canShare || !navigator.share) return false;
    if (!sampleFile) return true;
    return navigator.canShare({ files: [sampleFile] });
  } catch (_) {
    return false;
  }
}

async function fetchAsFile(item) {
  const url = `${API}/media/${item.id}/download`;
  const res = await axios.get(url, { responseType: "blob", headers: authHeaders() });
  const filename = item.filename || `meyecam_${item.id}`;
  return new File([res.data], filename, { type: res.data.type || "application/octet-stream" });
}

function triggerBrowserDownload(file) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Save a list of media items to the user's device.
 *  - On mobile (Web Share API available): open native share sheet so user can
 *    save all to Photos with one tap.
 *  - On desktop / unsupported: sequential downloads with a small inter-file delay.
 * onProgress(done, total) is called as each file finishes.
 */
export async function saveItemsToDevice(items, onProgress) {
  if (!items || items.length === 0) return { ok: false, reason: "empty" };
  const total = items.length;
  // Try Web Share API first when there are <= 20 files (iOS practical limit)
  if (total <= 20 && canWebShareFiles()) {
    try {
      const files = [];
      for (let i = 0; i < items.length; i++) {
        files.push(await fetchAsFile(items[i]));
        if (onProgress) onProgress(i + 1, total);
      }
      if (canWebShareFiles(files[0])) {
        await navigator.share({ files, title: "mEYEcam" });
        return { ok: true, mode: "share" };
      }
      // Fall through to sequential download if canShare(files) returns false
      for (const f of files) {
        triggerBrowserDownload(f);
        await sleep(450);
      }
      return { ok: true, mode: "sequential" };
    } catch (e) {
      // User cancelled share, or share failed — fall back to sequential
      if (e?.name === "AbortError") return { ok: false, reason: "cancelled" };
      // continue to fallback
    }
  }
  // Fallback: sequential downloads
  for (let i = 0; i < items.length; i++) {
    const f = await fetchAsFile(items[i]);
    triggerBrowserDownload(f);
    if (onProgress) onProgress(i + 1, total);
    await sleep(450);
  }
  return { ok: true, mode: "sequential" };
}

/** Download a ZIP of selected media. Backend streams; saves with FileSaver-style click. */
export async function downloadZipSelection(slug, ids) {
  const res = await axios.post(
    `${API}/album/${slug}/zip-selection`,
    { ids },
    { responseType: "blob", headers: { ...authHeaders(), "Content-Type": "application/json" } },
  );
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `meyecam-selection-${ids.length}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
